import React, { useState, useEffect } from 'react';
import { useInventory } from '../../../contexts/InventoryContext';
import { uploadFile, getPresignedUrl } from '../../../services/fileApi';
import inventoryApi from '../../../services/inventoryApi';
import toast from 'react-hot-toast';

const SupplierForm = ({ supplier, onSuccess, onCancel }) => {
  const { createSupplier, updateSupplier } = useInventory();

  const [formData, setFormData] = useState({
    name: '',
    supplierType: 'trade',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    isActive: true,
    notes: '',
    tinNumber: '',
    creditLimit: '',
    creditTermsDays: '',
    creditNotes: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [ledgerAccount, setLedgerAccount] = useState(null);
  const [syncingLedger, setSyncingLedger] = useState(false);

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name || '',
        supplierType: supplier.supplierType || 'trade',
        contactPerson: supplier.contactInfo?.contactPerson || '',
        email: supplier.contactInfo?.email || '',
        phone: supplier.contactInfo?.phone || '',
        address: supplier.address?.street || '',
        isActive: supplier.isActive !== undefined ? supplier.isActive : true,
        notes: supplier.notes || '',
        tinNumber: supplier.taxInfo?.tinNumber || '',
        creditLimit: supplier.creditInfo?.creditLimit ?? '',
        creditTermsDays: supplier.creditInfo?.creditTermsDays ?? '',
        creditNotes: supplier.creditInfo?.notes || ''
      });

      // Load existing attachments with presigned URLs
      if (supplier.attachments?.length > 0) {
        Promise.all(
          supplier.attachments.map(async (att) => {
            let previewUrl = null;
            try {
              const res = await getPresignedUrl(att.fileId, 3600);
              previewUrl = res.data?.data?.presignedUrl || res.data?.data?.url;
            } catch (err) {
              console.error('Failed to get presigned URL for attachment:', err);
            }
            return { ...att, previewUrl };
          })
        ).then(loaded => setAttachments(loaded));
      } else {
        setAttachments([]);
      }

      // Load ledger account status
      setLedgerAccount(supplier.ledgerAccount || null);
    } else {
      setAttachments([]);
      setLedgerAccount(null);
    }
  }, [supplier]);

  const handleAttachmentSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setAttachmentUploading(true);
    try {
      // Show local preview immediately
      const localPreview = URL.createObjectURL(file);
      const tempEntry = {
        fileId: null,
        originalName: file.name,
        documentType: 'Other',
        mimeType: file.type,
        previewUrl: localPreview,
        uploading: true
      };
      setAttachments(prev => [...prev, tempEntry]);

      // Upload to File Service
      const res = await uploadFile(file, {
        category: 'Other',
        entityType: 'supplier',
        title: file.name
      });

      if (res.data?.success) {
        const fileData = res.data.data;
        // Replace temp entry with real fileId
        setAttachments(prev =>
          prev.map(a =>
            a.uploading && a.originalName === file.name
              ? {
                  fileId: fileData.fileId,
                  originalName: fileData.originalName,
                  documentType: 'Other',
                  mimeType: fileData.mimeType,
                  previewUrl: localPreview,
                  uploading: false
                }
              : a
          )
        );
        toast.success('Document uploaded successfully');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.message || 'Failed to upload document');
      // Remove the failed temp entry
      setAttachments(prev =>
        prev.filter(a => !(a.uploading && a.originalName === file.name))
      );
    } finally {
      setAttachmentUploading(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handleRemoveAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handlePreviewClick = (att) => {
    console.log('Opening preview for:', att);
    setPreviewAttachment(att);
  };

  const handleSyncLedger = async () => {
    if (!supplier?._id) return;
    setSyncingLedger(true);
    try {
      const res = await inventoryApi.retryLedgerAccount(supplier._id);
      if (res.success) {
        const updated = res.data?.supplier?.ledgerAccount || res.data?.ledgerAccount;
        setLedgerAccount(updated);
        toast.success(
          updated?.syncStatus === 'synced'
            ? `Ledger account synced: ${updated.accountCode}`
            : 'Ledger account created successfully'
        );
      } else {
        toast.error(res.message || 'Failed to sync ledger account');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to sync ledger account');
    } finally {
      setSyncingLedger(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Supplier name is required';
    else if (formData.name.length > 200) newErrors.name = 'Name must be less than 200 characters';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Valid email is required';
    if (formData.notes && formData.notes.length > 500) newErrors.notes = 'Notes must be less than 500 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const submitData = {
        name: formData.name,
        supplierType: formData.supplierType,
        contactInfo: {
          contactPerson: formData.contactPerson,
          email: formData.email,
          phone: formData.phone
        },
        address: { street: formData.address },
        isActive: formData.isActive,
        notes: formData.notes,
        taxInfo: { tinNumber: formData.tinNumber.trim().toUpperCase() || null },
        creditInfo: {
          creditLimit: formData.creditLimit !== '' ? Number(formData.creditLimit) : null,
          creditTermsDays: formData.creditTermsDays !== '' ? Number(formData.creditTermsDays) : null,
          notes: formData.creditNotes
        },
        attachments: attachments
          .filter(a => a.fileId)
          .map(({ fileId, originalName, documentType, mimeType }) => ({
            fileId,
            originalName,
            documentType,
            mimeType
          }))
      };
      if (supplier) {
        await updateSupplier(supplier._id, submitData);
      } else {
        await createSupplier(submitData);
      }
      onSuccess();
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to save supplier');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-600 mb-1";
  const errorClass = "mt-0.5 text-xs text-red-500";

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-full px-4 py-6">
        <div className="relative w-full max-w-3xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-white">
                  {supplier ? 'Edit Supplier' : 'Create Supplier'}
                </h3>
              </div>
              <button
                type="button"
                onClick={onCancel}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="px-4 pt-3 pb-2 space-y-3">
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded p-2">
                  <p className="text-xs text-red-700">{errors.submit}</p>
                </div>
              )}

              {/* Basic Info */}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className={labelClass}>Supplier Name <span className="text-red-500">*</span></label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="ABC Suppliers Ltd."
                    className={`${inputClass} ${errors.name ? 'border-red-400' : ''}`}
                  />
                  {errors.name && <p className={errorClass}>{errors.name}</p>}
                </div>
                <div>
                  <label className={labelClass}>Supplier Type <span className="text-red-500">*</span></label>
                  <select
                    name="supplierType"
                    value={formData.supplierType}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="trade">Trade Supplier (goods & assets)</option>
                    <option value="expense">Expense / Service Supplier</option>
                    <option value="both">Both (goods, assets & services)</option>
                  </select>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className={labelClass}>Contact Person</label>
                  <input
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Email</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contact@supplier.com"
                    className={`${inputClass} ${errors.email ? 'border-red-400' : ''}`}
                  />
                  {errors.email && <p className={errorClass}>{errors.email}</p>}
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className={labelClass}>Address</label>
                <input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Business St, City, State, ZIP"
                  className={inputClass}
                />
              </div>

              {/* Tax & Credit */}
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1.5">Tax &amp; Credit Details</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className={labelClass}>TIN Number</label>
                    <input
                      name="tinNumber"
                      value={formData.tinNumber}
                      onChange={handleChange}
                      placeholder="e.g. 123456789"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Credit Limit (Rs)</label>
                    <input
                      name="creditLimit"
                      type="number"
                      value={formData.creditLimit}
                      onChange={handleChange}
                      placeholder="e.g. 500000"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Credit Terms (Days)</label>
                    <input
                      name="creditTermsDays"
                      type="number"
                      value={formData.creditTermsDays}
                      onChange={handleChange}
                      placeholder="e.g. 30"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <label className={labelClass}>Credit Notes</label>
                  <textarea
                    name="creditNotes"
                    rows={2}
                    value={formData.creditNotes}
                    onChange={handleChange}
                    placeholder="Additional credit terms or conditions..."
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Documents/Attachments */}
              <div>
                <label className={labelClass}>Document Attachment</label>
                <label
                  className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-indigo-400 transition-colors text-xs text-gray-500 mb-2"
                >
                  <svg className="w-6 h-6 mb-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="font-medium">Click to upload or drag & drop</span>
                  <span className="text-[10px] text-gray-400 mt-1">Image or PDF (max 10MB)</span>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={handleAttachmentSelect}
                    disabled={attachmentUploading}
                  />
                </label>

                {/* Preview cards for uploaded attachments */}
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((att, idx) => (
                      <div
                        key={att.fileId || idx}
                        className="relative group flex flex-col items-center gap-1"
                      >
                        <div className="relative w-20 h-20 border border-gray-200 rounded overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0">
                          {att.mimeType?.startsWith('image/') ? (
                            <img
                              src={att.previewUrl}
                              alt={att.originalName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-2xl">📄</div>
                          )}
                          {/* Remove button */}
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(idx)}
                            className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] hidden group-hover:flex items-center justify-center hover:bg-red-600 transition-colors"
                            title="Remove"
                          >
                            ×
                          </button>
                          {/* Preview button */}
                          <button
                            type="button"
                            onClick={() => handlePreviewClick(att)}
                            className="absolute top-0.5 left-0.5 w-4 h-4 bg-indigo-500 text-white rounded-full text-[10px] hidden group-hover:flex items-center justify-center hover:bg-indigo-600 transition-colors"
                            title="Preview"
                          >
                            👁
                          </button>
                          {/* Uploading indicator */}
                          {att.uploading && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <svg className="w-4 h-4 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        {/* Filename */}
                        <span className="text-[10px] text-gray-600 text-center max-w-[80px] truncate">{att.originalName}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className={labelClass}>Notes</label>
                <textarea
                  name="notes"
                  rows={2}
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Additional notes..."
                  className={`${inputClass} ${errors.notes ? 'border-red-400' : ''}`}
                />
                {errors.notes && <p className={errorClass}>{errors.notes}</p>}
              </div>

              {/* Ledger Account — Edit mode only */}
              {supplier && (
                <div className="border border-gray-200 rounded p-2.5 bg-gray-50">
                  <p className="text-xs font-medium text-gray-600 mb-2">Chart of Account (Ledger)</p>

                  {ledgerAccount?.syncStatus === 'synced' && ledgerAccount?.accountId ? (
                    // Synced state
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {ledgerAccount.accountCode}
                      </span>
                      <span className="text-xs text-gray-500 truncate">{ledgerAccount.accountName}</span>
                    </div>
                  ) : (
                    // Missing or failed state
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        {ledgerAccount?.syncStatus === 'failed' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            ⚠ Sync Failed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                            Not Linked
                          </span>
                        )}
                        {ledgerAccount?.lastSyncError && (
                          <p className="text-[10px] text-red-500 mt-1 max-w-[250px] truncate" title={ledgerAccount.lastSyncError}>
                            {ledgerAccount.lastSyncError}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleSyncLedger}
                        disabled={syncingLedger}
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                      >
                        {syncingLedger ? (
                          <>
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Syncing...
                          </>
                        ) : ledgerAccount?.syncStatus === 'failed' ? (
                          'Retry Sync'
                        ) : (
                          'Create'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Active + info row */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="h-3 w-3 text-indigo-600 border-gray-300 rounded"
                  />
                  <span className="text-xs text-gray-700">Active Supplier</span>
                </label>
                <span className="text-xs text-gray-400 italic">Supplier code will be auto-generated</span>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-gray-100">
              <button
                type="button"
                onClick={onCancel}
                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <>
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  supplier ? 'Update Supplier' : 'Create Supplier'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Fullscreen Preview Modal */}
      {previewAttachment && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/95 backdrop-blur-sm"
          onClick={() => setPreviewAttachment(null)}
        >
          <div
            className="relative w-full max-w-6xl max-h-[90vh] flex flex-col items-center justify-center"
            onClick={e => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors z-10"
              onClick={() => setPreviewAttachment(null)}
              title="Close"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Preview Content */}
            <div className="rounded-lg shadow-2xl bg-black border border-white/10 flex items-center justify-center w-full max-h-[75vh] overflow-hidden">
              {previewAttachment.mimeType?.startsWith('image/') ? (
                <img
                  src={previewAttachment.previewUrl}
                  alt={previewAttachment.originalName}
                  className="max-w-full max-h-[75vh] w-auto h-auto object-contain"
                />
              ) : (
                <iframe
                  src={previewAttachment.previewUrl}
                  className="w-full h-[75vh] bg-white"
                  title={previewAttachment.originalName}
                />
              )}
            </div>

            {/* Footer with Filename and Download */}
            <div className="mt-6 flex items-center justify-center gap-4 text-center">
              <span className="text-white text-sm font-medium max-w-md truncate">{previewAttachment.originalName}</span>
              {previewAttachment.previewUrl && (
                <a
                  href={previewAttachment.previewUrl}
                  download={previewAttachment.originalName}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs rounded-full font-medium transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierForm;
